import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import SelectorEmpresa from '../SelectorEmpresa';
import InscripcionPOS from '../../pages/InscripcionPOS';
import { useAuth } from '../../context/AuthContext';
import { inscripcionPOS as apiIns } from '../../api/posApi';
import AvisoMensualidad from '../holaveciLocal/AvisoMensualidad';

const DRAWER_WIDTH = 260;

// Rutas que un ADMIN_NEGOCIO puede ver sin haber inscrito el POS.
// La consola Superadmin no aplica aquí porque solo la abre superadmin.
const RUTAS_LIBRES = ['/inscripcion-pos'];

const AppLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  const toggleDrawer = () => setMobileOpen((prev) => !prev);

  // Gate: si el usuario es admin_negocio y su empresa NO tiene POS
  // activo, se le fuerza el formulario de inscripción. Superadmin no
  // se ve afectado (no es su rol activar por sí mismo).
  const [gateState, setGateState] = useState({ loading: true, forzarInscripcion: false });
  const esAdminNegocio = user?.entidadTipo === 'NEGOCIO';
  const esRutaLibre = RUTAS_LIBRES.includes(location.pathname);

  // D-24: también se fuerza la pantalla de pago si la mensualidad venció.
  const evaluarGate = useCallback(async () => {
    if (!esAdminNegocio) { setGateState({ loading: false, forzarInscripcion: false }); return; }
    try {
      const r = await apiIns.estado();
      setGateState({ loading: false, forzarInscripcion: !r.contrataPOS || !!r.bloqueadoPorMora });
    } catch {
      setGateState({ loading: false, forzarInscripcion: false });
    }
  }, [esAdminNegocio]);

  useEffect(() => { evaluarGate(); }, [evaluarGate]);

  // Cualquier llamada que responda 402 SUSCRIPCION_VENCIDA reevalúa el gate.
  useEffect(() => {
    const alVencer = () => evaluarGate();
    window.addEventListener('pos-suscripcion-vencida', alVencer);
    return () => window.removeEventListener('pos-suscripcion-vencida', alVencer);
  }, [evaluarGate]);

  const contenidoPrincipal = () => {
    if (gateState.loading) {
      return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;
    }
    if (gateState.forzarInscripcion && !esRutaLibre) {
      return <InscripcionPOS onActivado={() =>
        setGateState({ loading: false, forzarInscripcion: false })} />;
    }
    return (
      <>
        {esAdminNegocio && <AvisoMensualidad />}
        <Outlet />
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        drawerWidth={DRAWER_WIDTH}
        mobileOpen={mobileOpen}
        onClose={toggleDrawer}
        isMobile={isMobile}
      />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <TopBar onMenuClick={toggleDrawer} isMobile={isMobile} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            bgcolor: 'background.default',
            minWidth: 0,
            overflowX: 'auto',
          }}
        >
          {contenidoPrincipal()}
        </Box>
      </Box>

      {/* Dialog bloqueante para SUPERADMIN sin empresa elegida */}
      <SelectorEmpresa />
    </Box>
  );
};

export default AppLayout;
